import { Box, Flex, HStack, Text } from "@chakra-ui/react";
import React from "react";
import type { Point } from "src/components/DocumentViewer/V2/types";
import { PersonProfileLink } from "src/components/Links/PersonProfileLink";
import { AIValidationResult } from "src/hooks/useDocumentAI";
import * as GQL from "src/types/graphql";
import { AISuggestion } from "../AISuggestion";
import { IdentityDocumentMatchHint } from "../IdentityDocumentMatchHint";
import { getPreferredValue } from "../utils/aiSuggestionHelpers";
import {
  IdentityAnalyzeIdMatch,
  Profile,
  ProfileFieldType,
} from "../utils/analyzeIdFormMatchUtils";
import { AddressWithAISuggestion } from "./AddressWithAISuggestion";
import { useAddressAI } from "./useAddressAI";

type StudentPanelProps = {
  person: Profile | undefined;
  aiResultsMap?: Record<string, AIValidationResult> | null;
  identityAiProfileMatches?: IdentityAnalyzeIdMatch[];
  onFindInDocument?: (polygons: readonly (readonly Point[])[]) => void;
};

export const StudentPanel: React.FC<StudentPanelProps> = ({
  person,
  aiResultsMap,
  identityAiProfileMatches,
  onFindInDocument,
}) => {
  // Get person ID for constructing question IDs
  const personId = person?.id;

  // Get AI results for student fields using person:{person_id}:{field_name} format
  const birthDateAI = personId
    ? aiResultsMap?.[`person:${personId}:birth_date`]
    : null;

  // Use hook for address AI processing
  const { bestAddressAI, suggestedAddress } = useAddressAI(
    personId,
    aiResultsMap
  );

  const nameMatch = identityAiProfileMatches?.find(
    (match) => match.profileFieldType === ProfileFieldType.Name
  );
  const dobMatch = identityAiProfileMatches?.find(
    (match) => match.profileFieldType === ProfileFieldType.DateOfBirth
  );
  const addressMatch = identityAiProfileMatches?.find(
    (match) => match.profileFieldType === ProfileFieldType.Address
  );

  return (
    <Box mb={2}>
      <Text
        fontSize="2xl"
        fontWeight="bold"
        color="primary.700"
        mb={1}
        textAlign="center"
      >
        {person?.first_name || ""} {person?.last_name || ""}
      </Text>
      {nameMatch ? (
        <Flex justify="center">
          <IdentityDocumentMatchHint
            documentMatchText={nameMatch.documentMatchText}
            matchHighlights={nameMatch.matchHighlights}
            onFindInDocument={onFindInDocument}
          />
        </Flex>
      ) : null}
      <HStack justifyContent="center" spacing={2} mb={4}>
        <Text fontSize="sm" color="gray.600" textAlign="center">
          <Text as="span" textTransform="uppercase" display="inline">
            ID: {person?.id || "N/A"}
          </Text>
        </Text>
        <PersonProfileLink
          personId={person?.id}
          personType={GQL.person_type_enum.applicant}
        />
      </HStack>

      <Box my={3} textAlign="left">
        <Text fontSize="sm" color="gray.600" mb={1}>
          Date of birth
        </Text>
        <Text fontSize="md">
          {person?.birth_date
            ? person.birth_date.replace(/(\d{4})-(\d{2})-(\d{2})/, "$2/$3/$1")
            : "N/A"}
        </Text>

        {/* AI Suggestion for birth date */}
        {(() => {
          if (!birthDateAI) return null;

          const fieldRelevance =
            birthDateAI.field_relevance ?? birthDateAI.certainty ?? 0;
          if (fieldRelevance < 50) return null;

          const suggestedValue = getPreferredValue(birthDateAI);
          if (!suggestedValue || suggestedValue.trim() === "") return null;

          // Format current value to match display format (MM/DD/YYYY)
          const currentValue = person?.birth_date
            ? person.birth_date.replace(/(\d{4})-(\d{2})-(\d{2})/, "$2/$3/$1")
            : "";

          return (
            <AISuggestion
              suggestedValue={suggestedValue}
              currentValue={currentValue}
              certainty={birthDateAI.certainty}
              type="suggestion"
              isEditable={false}
              questionType="Date"
            />
          );
        })()}
        {dobMatch ? (
          <IdentityDocumentMatchHint
            documentMatchText={dobMatch.documentMatchText}
            matchHighlights={dobMatch.matchHighlights}
            onFindInDocument={onFindInDocument}
          />
        ) : null}
      </Box>

      <AddressWithAISuggestion
        person={person}
        bestAddressAI={bestAddressAI ?? null}
        suggestedAddress={suggestedAddress}
      />
      {addressMatch ? (
        <IdentityDocumentMatchHint
          documentMatchText={addressMatch.documentMatchText}
          matchHighlights={addressMatch.matchHighlights}
          onFindInDocument={onFindInDocument}
        />
      ) : null}
    </Box>
  );
};
